import React, { useState } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Alert,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';

const questions = [
  {
    id: 1,
    question: "Which assessment tool is most appropriate for evaluating dynamic balance?",
    options: [
      "Mini-Mental State Examination (MMSE)",
      "Timed Up and Go Test (TUG)",
      "Medication Review Form",
      "Visual Acuity Test"
    ],
    correct: "Timed Up and Go Test (TUG)",
    explanation: "The TUG test specifically measures dynamic balance and mobility by timing how long it takes a person to rise from a chair, walk 3 meters, turn around, walk back, and sit down."
  },
  {
    id: 2,
    question: "What is the first step in conducting a comprehensive fall risk assessment?",
    options: [
      "Physical examination",
      "Review of fall history",
      "Environmental assessment",
      "Medication review"
    ],
    correct: "Review of fall history",
    explanation: "Starting with fall history provides crucial context for all subsequent assessments and helps focus the evaluation on specific risk factors."
  },
  {
    id: 3,
    question: "Which cognitive assessment component is most relevant to fall risk?",
    options: [
      "Long-term memory",
      "Executive function",
      "Language skills",
      "Mathematical ability"
    ],
    correct: "Executive function",
    explanation: "Executive function, which includes attention, judgment, and planning, is crucial for safe mobility and avoiding fall risks."
  },
  {
    id: 4,
    question: "How often should fall risk assessments be conducted for high-risk residents?",
    options: [
      "Annually",
      "Every 6 months",
      "Every 3 months",
      "Monthly"
    ],
    correct: "Every 3 months",
    explanation: "High-risk residents should be assessed quarterly (every 3 months) to monitor changes and adjust interventions accordingly."
  }
];

const AssessmentQuiz = ({ onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [hasCalledComplete, setHasCalledComplete] = useState(false);

  const handleAnswer = (event) => {
    setAnswers({
      ...answers,
      [currentQuestion]: event.target.value
    });
    setShowExplanation(true);
  };

  const handleNext = () => {
    setShowExplanation(false);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const calculateScore = () => {
    let score = 0;
    Object.keys(answers).forEach(questionIndex => {
      if (answers[questionIndex] === questions[questionIndex].correct) {
        score++;
      }
    });
    return (score / questions.length) * 100;
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setShowExplanation(false);
    setHasCalledComplete(false);
  };

  if (showResult) {
    const score = calculateScore();
    // Call onComplete only once when quiz is finished with a passing score
    if (score >= 70 && onComplete && !hasCalledComplete) {
      setHasCalledComplete(true);
      onComplete();
    }
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Assessment Knowledge Check Complete!</Typography>
          <Typography variant="body1" gutterBottom>
            Your score: {score}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={score} 
            sx={{ my: 2, height: 10, borderRadius: 5 }}
          />
          {score >= 70 ? (
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              Congratulations! You've passed the quiz. You can now proceed to the next section.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
              You need a score of 70% or higher to proceed. Please try again.
            </Alert>
          )}
          <Button 
            variant="contained" 
            onClick={resetQuiz}
            sx={{ mt: 2 }}
          >
            Retake Quiz
          </Button>
        </CardContent>
      </Card>
    );
  }

  const question = questions[currentQuestion];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Fall Risk Assessment Quiz
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={(currentQuestion / questions.length) * 100} 
          sx={{ mb: 3, height: 10, borderRadius: 5 }}
        />
        
        <Typography variant="body1" gutterBottom>
          Question {currentQuestion + 1} of {questions.length}
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          {question.question}
        </Typography>

        <FormControl component="fieldset">
          <RadioGroup
            value={answers[currentQuestion] || ''}
            onChange={handleAnswer}
          >
            {question.options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {showExplanation && (
          <Alert 
            severity={answers[currentQuestion] === question.correct ? "success" : "error"}
            sx={{ mt: 2, mb: 2 }}
          >
            {answers[currentQuestion] === question.correct ? "Correct! " : "Incorrect. "}
            {question.explanation}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!answers[currentQuestion]}
          sx={{ mt: 2 }}
        >
          {currentQuestion < questions.length - 1 ? 'Next Question' : 'Show Results'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AssessmentQuiz;
